//============================================================================
// Name        : modals.ts
// Purpose     : Adjustment Requests (Tenant Admin) - srx popups: open / close,
//               Export form checks
// Author      : Senitron Team
// Copyright   : Copyright 2026 Senitron Corporation. All rights reserved.
//============================================================================
import { busy } from "./common";

const EMAIL = /^[\w+\-.]+@[a-z\d\-.]+\.[a-z]+$/i;

export class Modals {
  private root: HTMLElement;
  private opener: HTMLElement | null = null;

  constructor(root: HTMLElement, private t: (key: string, vars?: any) => string) {
    this.root = root;
    this.bindOpenClose();
    this.bindExport();
  }

  open(id: string, opener?: HTMLElement): void {
    const modal = document.getElementById(id);
    if (!modal) { return; }
    this.opener = opener || (document.activeElement as HTMLElement);
    modal.classList.add("open");
    modal.setAttribute("aria-hidden", "false");
    const focus = modal.querySelector("textarea, input:not([type=hidden]):not([disabled]), select, .modal-foot button:not([disabled])") as HTMLElement;
    if (focus) { window.setTimeout(() => focus.focus(), 50); }
  }

  close(id: string): void {
    const modal = document.getElementById(id);
    if (!modal || !modal.classList.contains("open")) { return; }
    modal.classList.remove("open");
    modal.setAttribute("aria-hidden", "true");
    if (this.opener) { this.opener.focus(); }
    this.opener = null;
  }

  private bindOpenClose(): void {
    this.root.addEventListener("click", (event: MouseEvent) => {
      const trigger = (event.target as HTMLElement).closest("[data-ar-open]") as HTMLElement;
      if (!trigger) { return; }
      event.preventDefault();
      this.open(trigger.getAttribute("data-ar-open"), trigger);
    });
    this.root.querySelectorAll(".modal-backdrop.ar-srx").forEach((backdrop: Element) => {
      backdrop.addEventListener("click", (event: MouseEvent) => {
        const target = event.target as HTMLElement;
        if (target === backdrop || target.closest("[data-close]")) { this.close(backdrop.id); }
      });
    });
    document.addEventListener("keydown", (event: KeyboardEvent) => {
      if (event.key !== "Escape") { return; }
      this.root.querySelectorAll(".modal-backdrop.ar-srx.open").forEach((modal: Element) => this.close(modal.id));
    });
  }

  // "Send it to a different address" needs a valid address; the button is
  // disabled once the form is sent (C-49) and the page reloads with the answer.
  private bindExport(): void {
    const modal = document.getElementById("ar-export-modal");
    const form = document.getElementById("ar-export-form") as HTMLFormElement;
    if (!modal || !form) { return; }
    const email = document.getElementById("ar-export-email-input") as HTMLInputElement;
    const box = modal.querySelector(".ar-export-email") as HTMLElement;
    const invalid = modal.querySelector(".ar-export-invalid") as HTMLElement;
    const submit = document.getElementById("ar-export-submit") as HTMLButtonElement;
    const otherSelected = () => {
      const checked = modal.querySelector('input[name="email_option"]:checked') as HTMLInputElement;
      return !!checked && checked.value === "other";
    };
    email.required = otherSelected();
    modal.addEventListener("change", (event: Event) => {
      if ((event.target as HTMLInputElement).name !== "email_option") { return; }
      box.classList.toggle("hidden", !otherSelected());
      email.required = otherSelected();
      if (otherSelected()) { email.focus(); }
    });
    form.addEventListener("submit", (event: Event) => {
      if (otherSelected() && !EMAIL.test(email.value.trim())) {
        event.preventDefault();
        invalid.classList.remove("hidden");
        return;
      }
      if (submit.disabled) { event.preventDefault(); return; }
      window.setTimeout(() => busy(submit, this.t("sending")), 0);
    });
  }
}
